import './Discussions.scss';
import React, { useContext, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Navbar from "../../components/Navbar/Navbar";
import Footer from "../../components/Footer/Footer";
import AuthContext from "../../context/AuthContext";
import SnackbarContext from "../../context/SnackbarContext";
import { authHeaders } from '../../utils/api';
import { API_BASE_URL } from '../../config';


function formatDate(iso) {
    if (!iso) return '';
    try {
        return new Date(iso).toLocaleString('ar', {
            year: 'numeric', month: 'short', day: 'numeric',
            hour: 'numeric', minute: '2-digit',
        });
    } catch {
        return iso;
    }
}


export default function Discussion() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { authTokens } = useContext(AuthContext);
    const { setSnackbar } = useContext(SnackbarContext);

    const [topic, setTopic] = useState(null);
    const [loading, setLoading] = useState(true);
    const [comment, setComment] = useState('');
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const r = await fetch(`${API_BASE_URL}/api/discussions/topics/${id}/`);
                if (r.status === 404) {
                    if (!cancelled) {
                        setSnackbar({ content: 'الموضوع غير موجود', success: false });
                        navigate('/discussions');
                    }
                    return;
                }
                if (!r.ok) throw new Error(`HTTP ${r.status}`);
                const data = await r.json();
                if (!cancelled) setTopic(data);
            } catch {
                if (!cancelled) setSnackbar({ content: 'تعذر تحميل الموضوع', success: false });
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [id, navigate, setSnackbar]);

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!comment.trim()) return;
        setSubmitting(true);
        try {
            const r = await fetch(`${API_BASE_URL}/api/discussions/topics/${id}/comments/`, {
                method: 'POST',
                headers: { ...authHeaders(authTokens), 'Content-Type': 'application/json' },
                body: JSON.stringify({ body: comment }),
            });
            if (r.status === 401) {
                setSnackbar({ content: 'يلزم تسجيل الدخول للتعليق', success: false });
                return;
            }
            const data = await r.json().catch(() => ({}));
            if (!r.ok) {
                setSnackbar({ content: data?.body?.[0] || data?.detail || 'تعذر إرسال التعليق', success: false });
                return;
            }
            // Append locally rather than refetching.
            setTopic(t => t ? { ...t, comments: [...(t.comments || []), data] } : t);
            setComment('');
        } catch {
            setSnackbar({ content: 'تعذر إرسال التعليق', success: false });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
        <Navbar />
        <main className='disc-page'>
            <div className='disc-container'>
                <Link to='/discussions' className='disc-back'>العودة إلى النقاشات</Link>

                {loading ? (
                    <p className='disc-empty'>جارٍ التحميل…</p>
                ) : topic ? (
                    <>
                        <article className='disc-topic'>
                            <h1>{topic.title}</h1>
                            <p className='disc-card-meta'>
                                بقلم <strong>{topic.author_username}</strong> · {formatDate(topic.created_at)}
                            </p>
                            <div className='disc-topic-body'>{topic.body}</div>
                        </article>

                        <section className='disc-comments'>
                            <h2>التعليقات ({topic.comments?.length || 0})</h2>
                            {(!topic.comments || topic.comments.length === 0) ? (
                                <p className='disc-empty'>لا توجد تعليقات بعد.</p>
                            ) : (
                                <ul className='disc-comment-list'>
                                    {topic.comments.map(c => (
                                        <li key={c.id} className='disc-comment'>
                                            <p className='disc-card-meta'>
                                                <strong>{c.author_username}</strong> · {formatDate(c.created_at)}
                                            </p>
                                            <div className='disc-comment-body'>{c.body}</div>
                                        </li>
                                    ))}
                                </ul>
                            )}

                            {authTokens ? (
                                <form className='disc-comment-form' onSubmit={onSubmit}>
                                    <textarea
                                        rows={4}
                                        maxLength={5000}
                                        placeholder='اكتب تعليقك…'
                                        value={comment}
                                        onChange={(e) => setComment(e.target.value)}
                                        required
                                    />
                                    <button type='submit' className='disc-button' disabled={submitting || !comment.trim()}>
                                        {submitting ? 'جارٍ النشر…' : 'انشر التعليق'}
                                    </button>
                                </form>
                            ) : (
                                <p className='disc-login-prompt'>
                                    <Link to='/register'>سجّل الدخول</Link> للمشاركة في النقاش.
                                </p>
                            )}
                        </section>
                    </>
                ) : null}
            </div>
        </main>
        <Footer />
        </>
    );
}
